import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * useTabQueryParam - A reusable hook to manage tab state via URL query parameter.
 * @param {string} paramName - The query parameter name to use (e.g. 'tab')
 * @param {string} defaultTab - The default tab value
 * @returns {[string, function]} [activeTab, setActiveTab]
 *
 * Usage:
 *   const [activeTab, setActiveTab] = useTabQueryParam('tab', 'members');
 *   setActiveTab('requests'); // updates the query param and tab state
 */
export default function useTabQueryParam(paramName, defaultTab) {
  const location = useLocation();
  const navigate = useNavigate();

  // Get current tab from query param or fallback to default
  const getTabFromQuery = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get(paramName) || defaultTab;
  }, [location.search, paramName, defaultTab]);

  // Setter updates the query param and pushes to history
  const setTab = useCallback(
    (tab) => {
      const params = new URLSearchParams(location.search);
      params.set(paramName, tab);
      navigate({ search: params.toString() }, { replace: false });
    },
    [location.search, navigate, paramName]
  );

  return [getTabFromQuery(), setTab];
}
